"use strict"

import React from "react"
import PropTypes from "prop-types"
const deepEqual = require("react-fast-compare")

import {
  isLTPM,
  run_config_wrapper as registration,
} from "../../../components/shared_components_registration"

import { getCurrentTime, parseDate } from "../../utils"
import { withPlotSettingsConfig } from "../../config/PlotSettingsConfigProvider"
import { withAnnotationsConfig } from "../../config/AnnotationConfigProvider"
import { getCommentsList } from "./GraphComponents/ActionLayer"

const {
  AbstractRunConfigClass,
  loadRunDetails,
  loadObservationSeries,
  loadRunFileList,
  logToServer,
  loadRunMicrobialCommunityData,
} = registration

/*
 * TODO -- flushing of run data that hasn't been wanted for a while.
 */
export function withWarehouseData(Component) {
  const container = class extends AbstractRunConfigClass {
    constructor(props) {
      super(props)

      this.state = {
        runData: {},

        /*
         * These are stored separately to avoid synchronization issues
         * without forcing a wait until this data is loaded.
         * */
        runFileLists: {},

        /*
         * Maximum number of tags to combine in a single observations request.
         */
        maxGroupedObservationRequests: 12,

        /*
         * Hint as to how many concurrent requests should be made to the warehouse.
         *
         * Note that most browsers also impose a hard limit (typically 6).
         */
        maxConcurrentObservationRequests: 5,
      }

      this.pendingRunMetaQueries = []
      this.loadRun = this.loadRun.bind(this)
      this.doLoadRun = this.doLoadRun.bind(this)

      this.loadGenericPendingObservations = this.loadGenericPendingObservations.bind(this)
      this.requestGenericObservations = this.requestGenericObservations.bind(this)

      this.pendingObservationQueries = []
      this.loadPendingObservations = this.loadPendingObservations.bind(this)
      this.requestObservations = this.requestObservations.bind(this)

      this.pendingSiteObservationQueries = []
      this.loadPendingSiteObservations = this.loadPendingSiteObservations.bind(this)
      this.requestSiteObservations = this.requestSiteObservations.bind(this)

      this.pendingSampleObservationQueries = []
      this.loadPendingSampleObservations = this.loadPendingSampleObservations.bind(this)
      this.requestSmpObservations = this.requestSmpObservations.bind(this)

      this.pendingMicrobialCommunityQueries = []
      this.loadPendingMicrobialCommunities = this.loadPendingMicrobialCommunities.bind(this)
      this.requestMicrobialCommunityData = this.requestMicrobialCommunityData.bind(this)

      this.reloadCheckTick = this.reloadCheckTick.bind(this)

      this.lowPriorityLoadQueue = []
      this.loadLowPriorityQuery = this.loadLowPriorityQuery.bind(this)

      this.loadingCount = 0
      this.ensureDataLoaded = this.ensureDataLoaded.bind(this)
    }

    /**
     * Increment the count of ongoing loads.
     */

    incLoading() {
      ++this.loadingCount
    }

    /**
     * Decrement the count of ongoing loads.
     */

    decLoading() {
      --this.loadingCount
      if (this.loadingCount < 0) {
        console.error("WARNING: Mismatched incLoading/decLoading")
        this.loadingCount = 0
      }
    }

    /**
     * Method to prioritize the loading of certain data before others.
     *
     * This works to assure all things load in because all data-loads currently update state upon completion and
     * thus will trigger another call in componentDidUpdate to this method which in turn loads more things that update
     * state (until all the queues are empty and all requested things are loaded).
     * */

    ensureDataLoaded() {
      if (this.loadingCount < this.state.maxConcurrentObservationRequests) {
        if (this.pendingRunMetaQueries.length > 0) {
          // Prioritize Run Metadata Loads
          this.pendingRunMetaQueries.forEach((_) => {
            // Keep issuing requests only if the current number of requests loading is lower than the limit
            if (this.loadingCount < this.state.maxConcurrentObservationRequests) {
              this.incLoading()
              this.doLoadRun()
            }
          })
        } else if (
          // If no runs are pending, load various types of observations next
          this.pendingObservationQueries.length > 0 ||
          this.pendingSiteObservationQueries.length > 0 ||
          this.pendingSampleObservationQueries.length > 0 ||
          this.pendingMicrobialCommunityQueries.length > 0
        ) {
          if (this.pendingObservationQueries.length > 0) this.loadPendingObservations()
          else if (this.pendingSiteObservationQueries.length > 0) this.loadPendingSiteObservations()
          else if (this.pendingSampleObservationQueries.length > 0) this.loadPendingSampleObservations()
          else if (this.pendingMicrobialCommunityQueries.length > 0) this.loadPendingMicrobialCommunities()
        } else if (this.lowPriorityLoadQueue.length > 0) {
          // If nothing else is pending, load anything considered 'low priority' (like file lists)
          this.lowPriorityLoadQueue.forEach((_) => {
            // Keep issuing requests only if the current number of requests loading is lower than the limit
            if (this.loadingCount < this.state.maxConcurrentObservationRequests) {
              this.incLoading()
              this.loadLowPriorityQuery()
            }
          })
        }
      }
    }

    reloadCheckTick() {
      const reloadInterval = this.props.reloadInterval
      if (!reloadInterval) return

      for (const runID of this.props.runs) {
        const runData = this.state.runData[runID]
        if (
          runData?.shouldReload &&
          runData?.loadedTime &&
          runData?.loadedTime.valueOf() + reloadInterval < getCurrentTime().valueOf()
        ) {
          this.loadRun(runID, true)
        }
      }
    }

    componentDidMount() {
      for (const run of this.props.runs || []) {
        this.loadRun(run, false)
      }

      this.reloadCheckID = window.setInterval(this.reloadCheckTick, 15e3)
    }

    componentWillUnmount() {
      if (this.reloadCheckID) {
        window.clearInterval(this.reloadCheckID)
      }
    }

    componentDidUpdate(prevProps, prevState) {
      if (this.props.runs !== prevProps.runs) {
        for (const run of this.props.runs || []) {
          this.loadRun(run, false)
        }
      }

      // Make sure all data is loaded in
      this.ensureDataLoaded()

      // If setTagsLoading method in props, the requesting component wants to be updated when tag loading is complete
      if (this.props.setTagsLoading && !deepEqual(this.state.runData, prevState.runData)) {
        const isLoading =
          (Object.values(this.state.runData) || [])
            .map((d) => {
              if (!d["observationData"]) return true
              return (
                (Object.values(d["observationData"]) || []).filter((t) => t.loading).map((t) => t.loading)
                  .length > 0
              )
            })
            .filter((d) => d).length > 0
        isLoading !== this.props.tagsLoading && this.props.setTagsLoading(isLoading)
      }
    }

    loadRun(runID, force) {
      /**
       * Initialize loading state of run and queue the loading of run metadata
       * */
      this.setState((state) => {
        const runData = state.runData[runID]
        if ((!runData || runData.err || force) && !this.pendingRunMetaQueries.includes(runID)) {
          this.pendingRunMetaQueries.push(runID)
          return {
            runData: {
              ...state.runData,
              [runID]: { id: runID, loading: true },
            },
          }
        } else {
          return null
        }
      })
    }

    doLoadRun() {
      /**
        Loads run metadata (e.g. inoculated_on, reactor, etc.) and adds several descriptive fields and
        utility methods to the runData object in set in state.

        The plot factory objects used to produce the desired plot reference some of these methods (as needed) in
        their 'ensureDataRequested' methods to ensure that the data required is loaded (e.g. requestObservations,
        requestSamples, requestSiteObservations, requestSmpObservations).

        Other methods are accessed by users or other processes to reload data on demand (e.g. forceReload,
        reloadAnnotations)
      */
      const runID = this.pendingRunMetaQueries[0]
      this.pendingRunMetaQueries.splice(0, 1)

      loadRunDetails(runID, this.props.live_run_cutoff, !!this.props.dashBoard)
        .then((data) => {
          this.props.updateRunAnnotationState &&
            data.annotations &&
            this.props.updateRunAnnotationState(data.annotations)
          this.setState((state) => ({
            runData: {
              ...state.runData,
              [runID]: {
                ...data,
                observationData: {},
                allLoading: false,
                makeObservationAccessor: makeObservationAccessor,
                requestObservations: this.requestObservations.bind(this, runID),
                makeMicrobialAbundanceAccessor: makeMicrobialAbundanceAccessor,
                requestMicrobialCommunityData: this.requestMicrobialCommunityData.bind(this, runID),
                forceReload: this.loadRun.bind(this, runID, true),
                shouldReload: !data.finished_on,
                loadedTime: getCurrentTime(),
                getCombinedActions: (
                  run, // passing in run as an argument as 'data' when referenced here could hold out of date information - e.g. annotations/comments reloaded separately
                ) =>
                  getCommentsList(
                    null,
                    this.props.show_site_actions,
                    run.site_actions,
                    this.props.show_actions,
                    run.actions,
                    this.props.show_critical_actions,
                    this.props.show_annotations,
                    run.annotations,
                  ),
                ...this.getApplicationSpecificRegistration(runID, data), // LL or LTPM application specific methods/variables
              },
            },
          }))
        })
        .catch((err) => {
          logToServer(err)
          console.error(err)
          this.setState((state) => ({
            runData: {
              ...state.runData,
              [runID]: {
                id: runID,
                err: "Error loading Run details",
                forceReload: this.loadRun.bind(this, runID, true),
              },
            },
          }))
        })
        .then((_) => {
          this.decLoading()
        })

      /**
       * Run files are not vital for page display and user experience so these are queued with
       * a low priority status to only be considered if nothing else is waiting to be loaded
       * */
      this.props.loadRunFiles &&
        !isLTPM &&
        this.lowPriorityLoadQueue.push(() =>
          loadRunFileList(runID)
            .then((fileList) => {
              this.setState((state) => ({
                runFileLists: {
                  ...state.runFileLists,
                  [runID]: fileList,
                },
              }))
            })
            .catch((err) => {
              logToServer(err)
              console.error(err)
            }),
        )
    }

    requestGenericObservations(runID, dataKey, tag, units, smp_src, force_reload, enqueueLoad, siteTagLoad) {
      /*
       * Because React can collapse multiple calls to setState, we can't simply check
       * this.state.observationData to determine whether the fetch has started.  Because
       * actually *triggering* the fetch is fast, it should be safe to put this inside
       * a state-transition function.
       */

      this.setState((state) => {
        const runData = state.runData[runID]
        if (!runData) {
          console.error(`Requested observations for unloaded runID: ${runID}`)
          return null
        }

        const tagInfo = runData?.observationTypesFlat?.[tag]

        const key = obsKey(tag, units, smp_src || null)

        // runData and currentData will be the same for [runID] keyed data and different for [site] keyed data
        const currentData = state.runData[dataKey] || {}
        if (
          currentData.observationData?.[key] &&
          this.props.live_run_cutoff === currentData.observationData?.[key]?.cutoff &&
          !force_reload
        ) {
          // Skip loading of tag data - Data already loaded, cutoff value has not changed, and not a forced reload
          return null
        } else {
          if (!tagInfo) {
            console.error(`Requested observations for non-existing tag ${tag}`)
            return null
          }

          const firstTagLoading =
            !siteTagLoad && Object.keys(currentData.observationData).length === 0 ? { allLoading: true } : {}

          // Queue loading of tag data
          enqueueLoad()

          // Update tag state to indicate that loading was requested
          return {
            runData: {
              ...state.runData,
              [dataKey]: {
                ...currentData,
                ...firstTagLoading,
                observationData: {
                  ...currentData.observationData,
                  [key]: {
                    loading: true,
                    cutoff: this.props.live_run_cutoff,
                  },
                },
              },
            },
          }
        }
      })
    }

    requestSmpObservations(runID, tag, units, smp_src, force_reload) {
      // Loads SampleObservation data - LanzaLake only
      this.requestGenericObservations(
        runID,
        runID,
        tag,
        units,
        smp_src,
        force_reload,
        this.enqueueLoadObservations.bind(
          this,
          { runID: runID, tag: tag, units: units, smp_src: smp_src },
          "Sample",
        ),
        false, //siteTagLoad
      )
    }

    requestSiteObservations(runID, tag, units, force_reload) {
      // Loads SiteObservation data - LanzaLake only
      this.requestGenericObservations(
        runID, // Use passed runID to lookup existence of tagInfo on run metadata return
        this.props.site,
        tag,
        units,
        null,
        force_reload,
        this.enqueueLoadObservations.bind(this, { site: this.props.site, tag: tag, units: units }, "Site"),
        true, //siteTagLoad
      )
    }

    requestObservations(runID, tag, units, force_reload = false) {
      // Loads Observation data
      this.requestGenericObservations(
        runID,
        runID,
        tag,
        units,
        null,
        force_reload,
        this.enqueueLoadObservations.bind(this, { runID: runID, tag: tag, units: units }),
        false, //siteTagLoad
      )
    }

    enqueueLoadObservations(varDict, obsType) {
      this[`pending${obsType ? obsType : ""}ObservationQueries`].push(varDict)
    }

    requestMicrobialCommunityData(runID, method, force_reload) {
      this.setState((state) => {
        const currentData = state.runData[runID]
        if (!currentData) {
          return null
        }
        // Skip if data already exists and not forcing a reload
        if (
          currentData.microbialCommunityData &&
          currentData.microbialCommunityData[method] &&
          !force_reload
        ) {
          return null
        }
        const newState = {
          runData: {
            ...state.runData,
            [runID]: {
              ...currentData,
              microbialCommunityData: {
                ...currentData.microbialCommunityData,
                [method]: {
                  loading: true,
                },
              },
            },
          },
        }

        this.enqueueLoadMicrobialCommunityData({ runID, method })
        return newState
      })
    }

    enqueueLoadMicrobialCommunityData(varDict) {
      if (
        !this.pendingMicrobialCommunityQueries.find(
          ({ runID, method }) => runID === varDict.runID && method === varDict.method,
        )
      ) {
        this.pendingMicrobialCommunityQueries.push(varDict)
      }
    }

    loadGenericPendingObservations(stateList, keyAccessor, loadSelf, obsType, getData, extraURL = null) {
      const maxGroupedObservationRequests = this.state.maxGroupedObservationRequests || 1
      const maxConcurrentObservationRequests = this.state.maxConcurrentObservationRequests || 2

      const reqsByBoxTag = {}
      for (const req of stateList) {
        // Boxcar tags to group requests that can be sent in a single API query.
        // Note that it's possible to boxcar requests with *different* units, but it's
        // not clear that it's possible to mix with and without-units requests.
        const boxTag = [req[keyAccessor], !!req.units, !!req.smp_src].join("___")
        let l = reqsByBoxTag[boxTag]
        if (!l) {
          l = reqsByBoxTag[boxTag] = []
        }
        l.push(req)
      }

      const updateRunTags = (dataKey, reqChunk, data, is_err) => {
        this.setState((state) => {
          let currentData = state.runData[dataKey]
          if (!currentData || currentData.loading) {
            // This could hit if you forceReload a run while it still has pending Obs loading or if somehow the
            // loading order gets out of sync (Run should fully load in before we request data for it)
            console.error(`Returned observations for unloaded ${keyAccessor}: ${dataKey}`)
            return null
          }

          const newObservationData = Object.assign({}, currentData.observationData)
          if (data) {
            for (const { tag, units, smp_src } of reqChunk) {
              const key = obsKey(tag, units, smp_src || null)
              if (is_err) {
                newObservationData[key] = {
                  err: data,
                }
              } else {
                newObservationData[key] = {
                  data: getData(data, smp_src)?.[tag] || [],
                  cutoff: newObservationData[key]?.cutoff,
                }
              }
            }

            const update_tag_loading = {}
            if (currentData.allLoading && !(obsType === "site")) {
              const tag_keys = Object.keys(newObservationData).length
              const loading_tag_keys = Object.values(newObservationData).filter((key) => key.loading).length
              update_tag_loading["allLoading"] = !!(loading_tag_keys > 0 && tag_keys === loading_tag_keys)
            }
            currentData = { ...currentData, ...update_tag_loading }
          }

          return {
            runData: {
              ...state.runData,
              [dataKey]: {
                ...currentData,
                observationData: newObservationData,
              },
            },
          }
        })
      }

      for (const reqBox of Object.values(reqsByBoxTag)) {
        const key = reqBox[0][keyAccessor]

        for (
          let requestIndex = 0;
          requestIndex < reqBox.length;
          requestIndex += maxGroupedObservationRequests
        ) {
          // Stop issuing requests if we've already made too many.
          if (this.loadingCount >= maxConcurrentObservationRequests) {
            break
          }

          const reqChunk = []
          for (
            let i = requestIndex;
            i < Math.min(reqBox.length, requestIndex + maxGroupedObservationRequests);
            ++i
          ) {
            const req = reqBox[i]
            reqChunk.push(req)

            const indexToRemove = stateList.indexOf(req)
            stateList.splice(indexToRemove, 1)
          }

          this.incLoading()
          loadObservationSeries(obsType === "site", obsType === "sample")(
            key,
            reqChunk.map((r) => r.tag).join(","),
            reqChunk[0].units && reqChunk.map((r) => r.units).join(","),
            reqChunk[0].smp_src && reqChunk.map((r) => r.smp_src).join(","),
            this.props.live_run_cutoff,
            extraURL,
          )
            .then((seriesData) => {
              updateRunTags(key, reqChunk, seriesData, false)
            })
            .catch((err) => {
              logToServer(err)
              console.error(err)
              updateRunTags(key, reqChunk, err, true)
            })
            .then((_) => this.decLoading())
        }
      }
    }

    loadPendingSampleObservations() {
      this.loadGenericPendingObservations(
        this.pendingSampleObservationQueries,
        "runID",
        this.loadPendingSampleObservations,
        "sample",
        (data, smp_src) => {
          return data?.[smp_src] || {}
        },
      )
    }

    loadPendingMicrobialCommunities() {
      const maxConcurrentObservationRequests = this.state.maxConcurrentObservationRequests || 2

      // Perform load logic in a filter, returning true when loading still needs to be done and false when loading is complete
      const remainingQueries = this.pendingMicrobialCommunityQueries.filter((microbialDataRequest) => {
        // Stop issuing requests if we've already made too many.
        if (this.loadingCount >= maxConcurrentObservationRequests) {
          return true
        }

        this.incLoading()

        loadRunMicrobialCommunityData(microbialDataRequest.runID, microbialDataRequest.method)
          .then((newMicrobialCommunityData) => {
            this.updateMicrobialCommunityData(
              microbialDataRequest.runID,
              microbialDataRequest.method,
              newMicrobialCommunityData,
            )
          })
          .catch((err) => {
            this.updateMicrobialCommunityData(microbialDataRequest.runID, microbialDataRequest.method, {
              err: err,
            })
            logToServer(err)
            console.error(err)
          })
          .then((_) => this.decLoading())

        return false
      })

      this.pendingMicrobialCommunityQueries = remainingQueries
    }

    updateMicrobialCommunityData(runID, method, newMicrobialCommunityData) {
      this.setState((state) => {
        const currentData = state.runData[runID]
        if (!currentData || currentData.loading) {
          // This could hit if you forceReload a run while it still has pending microbial community data loading
          // or if somehow the loading order gets out of sync (Run should fully load in before we request data for it)
          console.error(`Returned microbial community data for unloaded run ID: ${runID}`)
          return null
        }

        return {
          runData: {
            ...state.runData,
            [runID]: {
              ...currentData,
              microbialCommunityData: {
                ...state.runData[runID].microbialCommunityData,
                [method]: {
                  ...state.runData[runID].microbialCommunityData[method],
                  ...newMicrobialCommunityData,
                  loading: false,
                  error: "err" in newMicrobialCommunityData,
                },
              },
            },
          },
        }
      })
    }

    loadPendingSiteObservations() {
      this.loadGenericPendingObservations(
        this.pendingSiteObservationQueries,
        "site",
        this.loadPendingSiteObservations,
        "site",
        (data, _) => {
          return data || {}
        },
        this.props.activeSite?.urlReactors,
      )
    }

    loadPendingObservations() {
      this.loadGenericPendingObservations(
        this.pendingObservationQueries,
        "runID",
        this.loadPendingObservations,
        null,
        (data, _) => {
          return data || {}
        },
      )
    }

    loadLowPriorityQuery() {
      const [job] = this.lowPriorityLoadQueue.splice(0, 1)
      let result = null
      try {
        result = job()
      } catch (err) {
        console.error(err)
        logToServer(err)
      }

      if (result instanceof Promise) {
        result
          .catch((err) => {
            console.error(err)
            return null
          })
          .then((_) => this.decLoading())
      } else {
        this.decLoading()
      }
    }

    render() {
      const runData = this.state.runData,
        runFileLists = this.state.runFileLists,
        exposedRunData = {}

      for (const run of Object.keys(runData)) {
        const data = runData[run]
        const runFileObj = runFileLists[run] ? { fileList: runFileLists[run] } : {}
        if (isNaN(run)) {
          exposedRunData[run] = {
            ...data,
            observationAccessor: makeObservationAccessor(data.observationData),
            microbialAbundanceAccessor: makeMicrobialAbundanceAccessor(data.microbialCommunityData),
          }
        } else {
          exposedRunData[run] = {
            ...data,
            observationData: undefined,
            makeObservationAccessor: undefined,
            makeMicrobialAbundanceAccessor: undefined,

            ...runFileObj,

            // Make a convenience function for accessing observations while hiding
            // the internal state of the observation store.  makeObservationAccessor
            // is memoized per-run, so this won't cause a prop-transition on downstream
            // components unless observationData has changed.
            observationAccessor: data.makeObservationAccessor
              ? data.makeObservationAccessor(data.observationData)
              : dummyObservationAccessor,
            microbialAbundanceAccessor: data.makeMicrobialAbundanceAccessor
              ? data.makeMicrobialAbundanceAccessor(data.microbialCommunityData)
              : dummyMicrobialAbundanceAccessor,
          }
        }
      }

      return <Component {...this.props} runData={exposedRunData} />
    }
  }

  container.propTypes = {
    runs: PropTypes.arrayOf(PropTypes.number).isRequired,
    site: PropTypes.string,
    live: PropTypes.bool,
    live_run_cutoff: PropTypes.number,
    dashBoard: PropTypes.any,
    setTagsLoading: PropTypes.func,
  }

  return withPlotSettingsConfig(withAnnotationsConfig(container))
}

function obsKey(tag, units, smp_src = null) {
  if (!units) {
    return tag
  } else if (!smp_src) {
    return [tag, units || "default"].join("___")
  } else {
    return [tag, units, smp_src || "default"].join("___")
  }
}

function makeObservationAccessor(observationData) {
  return (tag, units, smp_src) => {
    return observationData[obsKey(tag, units, smp_src)]
  }
}

function dummyObservationAccessor(tag, units, smp_src) {
  return null
}

function makeMicrobialAbundanceAccessor(microbialCommunityData) {
  /*
    microbialCommunityData type
    {
      plot_data: { [species: string]: { day: string, value: number, timestamp: datetime }[] },
      species_order: string[],
      all_data: { [sample_id: string]: { [species: string]: number } }[]
    }
  */

  return (speciesName, method) =>
    microbialCommunityData &&
    microbialCommunityData[method] &&
    !microbialCommunityData[method].loading &&
    !microbialCommunityData[method].error &&
    microbialCommunityData[method]?.plot_data?.[speciesName]
      ? {
          data: microbialCommunityData[method].plot_data[speciesName].map((sample) => ({
            ...sample,
            timestamp: parseDate(sample.timestamp),
          })),
        }
      : {}
}

function dummyMicrobialAbundanceAccessor(speciesName) {
  return null
}
